import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { XStack, YStack } from "tamagui";

import { AdminPageScaffold, AdminStatusPill } from "@/components/AdminScaffold";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppEmptyState,
  AppLoadingState,
  AppSection,
  AppSectionHeader,
  AppText,
  AppTextArea,
  kindredFontFamilies,
} from "../../ui-foundation";

type AuthSupportStatus = "open" | "in_progress" | "resolved";

type AuthSupportRequest = {
  id: string;
  name: string | null;
  email: string;
  issue: string;
  source: "web" | "ios" | "android" | "unknown";
  status: AuthSupportStatus;
  email_delivery_status: "pending" | "sent" | "failed";
  admin_note: string | null;
  reviewed_at: string | null;
  created_at: string;
  reviewer: {
    display_name: string | null;
  } | null;
};

const statusTone: Record<AuthSupportStatus, "primary" | "secondary" | "success"> = {
  open: "primary",
  in_progress: "secondary",
  resolved: "success",
};

const statusLabel: Record<AuthSupportStatus, string> = {
  open: "Open",
  in_progress: "In progress",
  resolved: "Resolved",
};

const AdminLoginSupport = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState<AuthSupportRequest[]>([]);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState<string | null>(null);

  const loadRequests = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("auth_support_requests")
      .select(
        "id, name, email, issue, source, status, email_delivery_status, admin_note, reviewed_at, created_at, reviewer:profiles!auth_support_requests_reviewed_by_fkey(display_name)",
      )
      .order("created_at", { ascending: false });

    if (error) {
      toast.error("Could not load login support requests.");
      setLoading(false);
      return;
    }

    const rows = (data ?? []) as unknown as AuthSupportRequest[];
    setRequests(rows);
    setNotes(Object.fromEntries(rows.map((request) => [request.id, request.admin_note ?? ""])));
    setLoading(false);
  };

  useEffect(() => {
    void loadRequests();
  }, []);

  const groupedRequests = useMemo(
    () => ({
      open: requests.filter((request) => request.status !== "resolved"),
      history: requests.filter((request) => request.status === "resolved"),
    }),
    [requests],
  );

  const updateStatus = async (requestId: string, status: AuthSupportStatus) => {
    if (!user?.id) return;

    setWorkingId(requestId);
    const { error } = await supabase
      .from("auth_support_requests")
      .update({
        status,
        admin_note: notes[requestId]?.trim() || null,
        reviewed_by: user.id,
        reviewed_at: new Date().toISOString(),
      })
      .eq("id", requestId);
    setWorkingId(null);

    if (error) {
      toast.error("Could not update that support request.");
      return;
    }

    toast.success("Login support request updated.");
    void loadRequests();
  };

  const replyToRequest = (request: AuthSupportRequest) => {
    const subject = encodeURIComponent("Your Kindred login support request");
    const greeting = request.name?.trim() ? `Hi ${request.name.trim()},` : "Hello,";
    const body = encodeURIComponent(
      `${greeting}\n\nI am following up on your Kindred login support request.\n\n`,
    );
    window.location.href = `mailto:${request.email}?subject=${subject}&body=${body}`;
  };

  const RequestCard = ({ request }: { request: AuthSupportRequest }) => (
    <AppCard>
      <AppCardBody>
        <XStack alignItems="flex-start" justifyContent="space-between" gap="$sm" flexWrap="wrap">
          <YStack flex={1} minWidth={220} gap={4}>
            <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$lg" lineHeight="$lg">
              {request.name || "Name not provided"}
            </AppText>
            <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
              {request.email}
            </AppText>
            <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
              {request.source === "unknown" ? "Platform not known" : request.source.toUpperCase()}
              {" · "}
              Requested {format(new Date(request.created_at), "d MMM yyyy, HH:mm")}
              {request.email_delivery_status === "failed" ? " · Admin email failed" : ""}
            </AppText>
          </YStack>
          <AdminStatusPill tone={statusTone[request.status]}>
            {statusLabel[request.status]}
          </AdminStatusPill>
        </XStack>

        <AppCard backgroundColor="$background">
          <AppCardBody>
            <AppText
              color="$primary"
              fontFamily={kindredFontFamilies.sansBold}
              fontSize="$xs"
              lineHeight="$sm"
              textTransform="uppercase"
              letterSpacing={1.2}
            >
              What is happening
            </AppText>
            <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md" whiteSpace="pre-wrap">
              {request.issue}
            </AppText>
          </AppCardBody>
        </AppCard>

        <YStack gap="$xs">
          <AppText
            color="$primary"
            fontFamily={kindredFontFamilies.sansBold}
            fontSize="$xs"
            lineHeight="$sm"
            textTransform="uppercase"
            letterSpacing={1.2}
          >
            Internal note
          </AppText>
          <AppTextArea
            value={notes[request.id] ?? ""}
            onChangeText={(value) =>
              setNotes((current) => ({ ...current, [request.id]: value.slice(0, 1000) }))
            }
            placeholder="Record what you checked or sent."
            minHeight={104}
          />
        </YStack>

        <AppButtonRow>
          <AppButton
            type="button"
            tone="outlinePrimary"
            size="sm"
            onPress={() => replyToRequest(request)}
          >
            Reply by email
          </AppButton>
          {request.status === "open" ? (
            <AppButton
              type="button"
              tone="outline"
              size="sm"
              onPress={() => updateStatus(request.id, "in_progress")}
              disabled={workingId === request.id}
            >
              {workingId === request.id ? "Saving..." : "Mark in progress"}
            </AppButton>
          ) : null}
          {request.status !== "resolved" ? (
            <AppButton
              type="button"
              size="sm"
              onPress={() => updateStatus(request.id, "resolved")}
              disabled={workingId === request.id}
            >
              {workingId === request.id ? "Saving..." : "Mark resolved"}
            </AppButton>
          ) : (
            <AppButton
              type="button"
              tone="outline"
              size="sm"
              onPress={() => updateStatus(request.id, "open")}
              disabled={workingId === request.id}
            >
              Reopen
            </AppButton>
          )}
        </AppButtonRow>

        {request.reviewer?.display_name && request.reviewed_at ? (
          <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
            Last updated by {request.reviewer.display_name} on{" "}
            {format(new Date(request.reviewed_at), "d MMM yyyy, HH:mm")}
          </AppText>
        ) : null}
      </AppCardBody>
    </AppCard>
  );

  return (
    <AdminPageScaffold title="Login support">
      {loading ? (
        <AppLoadingState title="Loading login support" body="Checking for people who cannot access Kindred." />
      ) : (
        <>
          <AppSection>
            <AppSectionHeader
              title="Open requests"
              subtitle="Reply to the person, record what you checked, then mark the request resolved."
            />
            {groupedRequests.open.length ? (
              <YStack gap="$sm">
                {groupedRequests.open.map((request) => (
                  <RequestCard key={request.id} request={request} />
                ))}
              </YStack>
            ) : (
              <AppEmptyState title="No open login requests" body="Nothing is waiting for a response." />
            )}
          </AppSection>

          <AppSection>
            <AppSectionHeader
              title="Resolved"
              subtitle="Recent login support history remains here for reference."
            />
            {groupedRequests.history.length ? (
              <YStack gap="$sm">
                {groupedRequests.history.map((request) => (
                  <RequestCard key={request.id} request={request} />
                ))}
              </YStack>
            ) : (
              <AppEmptyState title="No resolved requests yet" body="Completed support requests will appear here." />
            )}
          </AppSection>
        </>
      )}
    </AdminPageScaffold>
  );
};

export default AdminLoginSupport;
